import React from "react";

import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import TimelineIcon from "@mui/icons-material/Timeline";
import StraightenIcon from "@mui/icons-material/Straighten";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import CancelIcon from "@mui/icons-material/Cancel";
import CalculateIcon from "@mui/icons-material/Calculate";

import { Stack, Typography, IconButton } from "@mui/material";
import { DepthEstimationStatus } from "../../../../context/model-viewer-context";

interface ModelViewerControlBarProps {
    title: string;
    showAreaTool: boolean;
    pointsCount: number;
    canCalculateDepth: boolean;
    depthStatus: DepthEstimationStatus;
    disableDownload: boolean;
    onAreaToolToggle: () => void;
    onMeasureToolToggle: () => void;
    onCenterModel: () => void;
    onResetPoints: () => void;
    onCalculateDepth: () => void;
    onDownload: () => void;
    onClose: () => void;
}

const ModelViewerControlBar: React.FC<ModelViewerControlBarProps> = ({
    title,
    showAreaTool,
    pointsCount,
    canCalculateDepth,
    depthStatus,
    disableDownload,
    onAreaToolToggle,
    onMeasureToolToggle,
    onCenterModel,
    onResetPoints,
    onCalculateDepth,
    onDownload,
    onClose,
}) => {
    return (
        <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            sx={{
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                paddingY: 1,
                flexShrink: 0,
            }}
        >
            <Stack sx={{ paddingLeft: 2 }}>
                <Typography
                    variant="h5"
                    sx={{
                        color: "white",
                        flexShrink: 0,
                    }}
                >
                    {title}
                </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                    onClick={onAreaToolToggle}
                    sx={{
                        color: showAreaTool ? "#2196f3" : "white",
                        backgroundColor: showAreaTool
                            ? "rgba(33, 150, 243, 0.1)"
                            : "transparent",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.15)",
                        },
                    }}
                    title="Area Tool"
                >
                    <TimelineIcon />
                </IconButton>

                <IconButton
                    onClick={onMeasureToolToggle}
                    sx={{
                        color: !showAreaTool ? "#2196f3" : "white",
                        backgroundColor: !showAreaTool
                            ? "rgba(33, 150, 243, 0.1)"
                            : "transparent",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.15)",
                        },
                    }}
                    title="Distance Tool"
                >
                    <StraightenIcon />
                </IconButton>

                <IconButton
                    onClick={onCenterModel}
                    sx={{
                        color: "white",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.15)",
                        },
                    }}
                    title="Center Model"
                >
                    <CenterFocusStrongIcon />
                </IconButton>

                <IconButton
                    onClick={onResetPoints}
                    disabled={pointsCount === 0}
                    sx={{
                        color: pointsCount > 0 ? "white" : "rgba(255,255,255,0.3)",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.15)",
                        },
                        "&:disabled": {
                            color: "rgba(255,255,255,0.3)",
                        },
                    }}
                    title="Reset Points"
                >
                    <CancelIcon />
                </IconButton>

                <IconButton
                    onClick={onCalculateDepth}
                    disabled={
                        !canCalculateDepth || depthStatus === DepthEstimationStatus.IN_PROGRESS
                    }
                    sx={{
                        color: canCalculateDepth ? "white" : "rgba(255,255,255,0.3)",
                        backgroundColor:
                            depthStatus === DepthEstimationStatus.COMPLETED
                                ? "rgba(76, 175, 80, 0.1)"
                                : "transparent",
                        "&:hover": {
                            backgroundColor: canCalculateDepth
                                ? "rgba(255,255,255,0.15)"
                                : "transparent",
                        },
                        "&:disabled": {
                            color: "rgba(255,255,255,0.3)",
                        },
                    }}
                    title={
                        depthStatus === DepthEstimationStatus.IN_PROGRESS
                            ? "Calculating Depth..."
                            : depthStatus === DepthEstimationStatus.COMPLETED
                                ? "Depth Calculated"
                                : "Calculate Depth"
                    }
                >
                    <CalculateIcon />
                </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ paddingRight: 1 }}>
                <IconButton
                    aria-label="download"
                    disabled={disableDownload}
                    onClick={onDownload}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.15)",
                        },
                    }}
                >
                    <DownloadIcon />
                </IconButton>

                <IconButton
                    onClick={onClose}
                    aria-label="close"
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.15)",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Stack>
        </Stack>
    );
};

export default ModelViewerControlBar;